
import { supabase } from "@/lib/supabase";
import { PlatformIntelligence } from "@/utils/platform-intelligence";

export async function generateAIResponse(
  type: "initial" | "seller_response" | "follow_up",
  refundRequest: any,
  previousMessages: any[] = [],
  tone?: string,
  customInstructions?: string,
  isDemo: boolean = false
): Promise<any> {
  const startTime = performance.now();
  
  try {
    console.log('🚀 Generating AI response with performance tracking:', { 
      type, 
      isDemo, 
      tone, 
      customInstructions,
      messageFormat: refundRequest.message_format,
      timestamp: new Date().toISOString()
    });
    
    // Get message format from conversation or default to email
    const messageFormat = refundRequest.message_format || "email";
    console.log('✅ Using message format:', messageFormat);
    
    // Analyze platform and message context
    const platformStartTime = performance.now();
    const platformAnalysis = PlatformIntelligence.analyze(
      refundRequest.merchant,
      refundRequest.order_number,
      refundRequest.issue,
      refundRequest.amount,
      refundRequest.region
    );
    const platformTime = performance.now() - platformStartTime;
    
    console.log('🔍 Platform analysis completed:', {
      ...platformAnalysis,
      analysisTime: `${platformTime.toFixed(0)}ms`
    });
    
    const payload = {
      type,
      merchant: refundRequest.merchant,
      product: refundRequest.product || refundRequest.merchant,
      amount: refundRequest.amount,
      issue: refundRequest.issue,
      orderNumber: refundRequest.order_number,
      tone,
      customInstructions,
      previousMessages,
      region: refundRequest.region,
      isDemo,
      messageFormat, // Pass the message format to the edge function
      messageContext: platformAnalysis.messageContext // Pass the message context
    };

    console.log('📤 Calling edge function with optimized payload:', {
      type: payload.type,
      messageFormat: payload.messageFormat,
      payloadSize: JSON.stringify(payload).length
    });

    // Use Supabase client to invoke the edge function instead of direct fetch
    const apiStartTime = performance.now();
    const { data, error } = await supabase.functions.invoke('analyze-refund', {
      body: payload
    });
    const apiTime = performance.now() - apiStartTime;

    if (error) {
      console.error('❌ Supabase function error:', error);
      throw new Error(`Function Error: ${error.message}`);
    }

    const totalTime = performance.now() - startTime;
    
    console.log('📥 AI response received with performance metrics:', {
      success: data?.success,
      messageFormat: data?.messageFormat || 'not specified',
      responseLength: data?.response?.length || 0,
      timing: {
        total: `${totalTime.toFixed(0)}ms`,
        platform: `${platformTime.toFixed(0)}ms`,
        api: `${apiTime.toFixed(0)}ms`,
        percentage: {
          platform: `${((platformTime / totalTime) * 100).toFixed(1)}%`,
          api: `${((apiTime / totalTime) * 100).toFixed(1)}%`
        }
      }
    });
    
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from AI service');
    }
    
    return data;
  } catch (error) {
    console.error('❌ Error generating AI response:', error);
    
    // Provide more specific error messages
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Network error: Unable to connect to AI service. Please check your connection and try again.');
    } else if (error.message.includes('Function Error')) {
      throw error; // Re-throw function errors as-is
    } else if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
      throw new Error('Server response error: Invalid data format received. Please try again.');
    }
    
    throw error;
  }
}
